import { faEdit, faTrashAlt } from '@fortawesome/free-regular-svg-icons'
import { faDownload, faProjectDiagram } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ButtonGroup } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { AssignDialogProps } from '../../components/AssignDialog'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import { FlipCard } from '../../components/FlipCard'
import ProtectedButton from '../../components/ProtectedButton'
import { SelectDialogProps } from '../../components/SelectDialog'
import Loading from '../../layout/Loading'
import ActiveDirectoryService from '../../service/ActiveDirectoryService'
import GatewayType from '../GatewayType'

const adMappingPermission = Authorization.userManagement.adMapping

const ActiveDirectoryMapping: GatewayType = () => {
  const { t } = useTranslation()
  const [adMapping, setAdMapping] = useState([] as any[])
  const [loading, setLoading] = useState(true)
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter, assignDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    ActiveDirectoryService.getAdMapping(setAdMapping).finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('description.text'),
        dataField: 'description',
        sort: true,
        classes: 'position-relative',
        formatter: (cellData: any, row: any) => (
          <ProtectedButton
            variant='link'
            className='text-decoration-none pl-0 stretched-link text-left'
            tooltip={t('edit')}
            onClick={() => editAdMappingDialog(t, editDialogPropsSetter, setAdMapping, row)}>
            <FontAwesomeIcon icon={faEdit} /> {cellData}
          </ProtectedButton>
        ),
      },
      {
        text: t('dn.text'),
        dataField: 'dn',
        sort: true,
      },
      {
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={adMappingPermission}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('adMapping.assignRoles')}
                onClick={() => assignRolesDialog(t, assignDialogPropsSetter, row)}>
                <FontAwesomeIcon icon={faProjectDiagram} />
              </ProtectedButton>
              <ProtectedButton
                permissions={adMappingPermission}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('delete')}
                onClick={() => deleteAdMappingDialog(t, confirmDialogPropsSetter, setAdMapping, row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, confirmDialogPropsSetter, editDialogPropsSetter, assignDialogPropsSetter])

  const defaultSorted = [
    {
      dataField: 'description',
      order: 'asc',
    },
  ]

  return AuthService.hasPermission(userPermissions, adMappingPermission) ? (
    <Loading isLoading={loading}>
      <DataTable
        data={adMapping}
        columns={columns}
        caption={t('adMapping.text')}
        helpLink={t('adMapping.helpLink')}
        captionButtons={[
          {
            onClick: () => createAdMappingDialog(t, editDialogPropsSetter, setAdMapping),
            text: t('new'),
            permissions: adMappingPermission,
          },
          {
            onClick: () => importAdMappingDialog(t, editDialogPropsSetter, setAdMapping),
            text: t('import'),
            permissions: adMappingPermission,
            icon: faDownload,
          },
        ]}
        searchPlaceholder='Search Ad Mapping'
        defaultSorted={defaultSorted}
      />
    </Loading>
  ) : (
    <></>
  )
}

function importAdMappingDialog(t: TFunction, editDialogPropsSetter: any, setAdMapping: React.Dispatch<React.SetStateAction<any[]>>) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('adMapping.importHeader'),
    labelGridSize: 3,
    onSubmit: ActiveDirectoryService.importAdMapping,
    onSubmitExtraArgs: [t, setAdMapping],
    data: [
      {
        id: 'searchBase',
        type: 'text',
        label: t('searchBase.text'),
        placeholder: t('searchBase.text'),
        requiredMessage: t('searchBase.inValidMessage'),
        required: true,
      },
    ],
  }
  editDialogPropsSetter(editDialogProps)
}

function createAdMappingDialog(t: TFunction, editDialogPropsSetter: any, setAdMapping: React.Dispatch<React.SetStateAction<any[]>>) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('adMapping.createHeader'),
    labelGridSize: 3,
    onSubmit: ActiveDirectoryService.createAdMapping,
    onSubmitExtraArgs: [t, setAdMapping],
    data: [
      {
        id: 'description',
        type: 'text',
        label: t('description.text'),
        placeholder: t('description.text'),
        requiredMessage: t('description.inValidMessage'),
        required: true,
      },
      {
        id: 'dn',
        type: 'text',
        label: t('dn.text'),
        placeholder: t('dn.text'),
        requiredMessage: t('dn.inValidMessage'),
        required: true,
      },
    ],
  }
  editDialogPropsSetter(editDialogProps)
}

function editAdMappingDialog(t: TFunction, editDialogPropsSetter: any, setAdMapping: React.Dispatch<React.SetStateAction<any[]>>, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('adMapping.editHeader'),
    onSubmit: ActiveDirectoryService.changeAdMapDescription,
    onSubmitExtraArgs: [t, setAdMapping],
    data: [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'description',
        value: row.description,
        type: 'text',
        label: t('description.text'),
        placeholder: t('description.text'),
        requiredMessage: t('description.inValidMessage'),
        required: true,
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function deleteAdMappingDialog(t: TFunction, confirmDialogPropsSetter: any, setAdMapping: any, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('adMapping.deleteHeader'),
    body: t('adMapping.deleteBody', { description: row.description }),
    submitText: t('yes'),
    onSubmit: ActiveDirectoryService.deleteAdMapping,
    onSubmitExtraArgs: [row, t, setAdMapping],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function assignRolesDialog(t: TFunction, assignDialogPropsSetter: any, row: any) {
  ActiveDirectoryService.getRolesWithAssigned(row.id).then((roles) => {
    const assignDialogProps: AssignDialogProps = {
      show: true,
      header: t('adMapping.assignHeader', { description: row.description }),
      onSubmit: ActiveDirectoryService.assignRoles,
      onSubmitExtraArgs: [row, t],
      data: roles,
      columns: [
        {
          text: t('name.text'),
          dataField: 'name',
          sort: true,
        },
        {
          text: t('description.text'),
          dataField: 'description',
          sort: true,
        },
        {
          dataField: 'isAssigned',
          text: t('assign'),
          isAssignField: true,
        },
      ],
    }

    assignDialogPropsSetter(assignDialogProps)
  })
}

function selectAdMappingDialog(t: TFunction, selectDialogPropsSetter: any, adMapping: any[], onSubmit: any, onSubmitExtraArgs?: any[]) {
  const selectDialogProps: SelectDialogProps = {
    show: true,
    header: t('adMapping.selectHeader'),
    data: adMapping,
    keyField: 'description',
    label: t('adMapping.selectHeader'),
    submitText: t('select'),
    onSubmit,
    onSubmitExtraArgs,
    cancelText: t('cancel'),
  }
  selectDialogPropsSetter(selectDialogProps)
}

export const AdMappingFlipCard = () => {
  const [adMapping, setAdMapping] = useState([] as any[])
  const { t } = useTranslation()
  const { userPermissions } = useContext(AppContext)
  const { selectDialogPropsSetter, editDialogPropsSetter, confirmDialogPropsSetter, assignDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    if (AuthService.hasPermission(userPermissions, adMappingPermission)) {
      ActiveDirectoryService.getAdMapping(setAdMapping)
    }
  }, [userPermissions])

  function handleclick(onSubmit: any, onSubmitExtraArgs?: any[] | undefined) {
    selectAdMappingDialog(t, selectDialogPropsSetter, adMapping, onSubmit, onSubmitExtraArgs)
  }

  return (
    <FlipCard
      permissions={adMappingPermission}
      headerText={t('adMapping.text')}
      headerLink='./userManagement/activeDirectoryMapping'
      flipFrontContent={
        <ButtonGroup vertical>
          <ProtectedButton
            permissions={adMappingPermission}
            variant='link'
            className='m-1'
            onClick={() => createAdMappingDialog(t, editDialogPropsSetter, setAdMapping)}>
            {t('adMapping.createHeader')}
          </ProtectedButton>
          <ProtectedButton
            permissions={adMappingPermission}
            variant='link'
            className='m-1'
            onClick={() => createAdMappingDialog(t, editDialogPropsSetter, setAdMapping)}>
            {t('adMapping.createHeader')}
          </ProtectedButton>
          <ProtectedButton
            permissions={adMappingPermission}
            variant='link'
            className='m-1'
            onClick={() => handleclick(editAdMappingDialog, [t, editDialogPropsSetter, setAdMapping])}>
            {t('adMapping.editHeader')}
          </ProtectedButton>
          <ProtectedButton
            permissions={adMappingPermission}
            variant='link'
            className='m-1'
            onClick={() => handleclick(assignRolesDialog, [t, assignDialogPropsSetter])}>
            {t('adMapping.assignRoles')}
          </ProtectedButton>
          <ProtectedButton
            permissions={adMappingPermission}
            variant='link'
            className='m-1'
            onClick={() => handleclick(deleteAdMappingDialog, [t, confirmDialogPropsSetter, setAdMapping])}>
            {t('adMapping.deleteHeader')}
          </ProtectedButton>
        </ButtonGroup>
      }
    />
  )
}

export default ActiveDirectoryMapping
